import { Box, Button, Grid, IconButton, Typography } from '@mui/material';
import { ProceedSaveLater, SubHeading } from '../investors/components';
import { MFTextField } from '../../lib/formik';
import { Formik, FormikHelpers, validateYupSchema, yupToFormErrors } from 'formik';
import MFCheckbox from '../../lib/formik/Checkbox';
import {
  applicationComparison,
  getAddressData,
  getAddressFields,
  getApplicantName,
  preventSpecialCharacters,
  permanentAddressSameAsCorresponding,
  saveForLater,
  removeSingleQuote,
  ifPushToXalt,
} from '../../utils/utilityFunctions';
import { ApplicantAddressType, ContactPerson } from '../../redux-store/types/api-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { RootStateType } from '../../redux-store/reducers';
import { Address } from '../investors/contactDetails';
import { updateApplication } from '../../redux-store/actions/application';
import { nonIndividualContactDetailsSchema } from '../../utils/schema';
import { CountryCodesDropDown } from '../commonComponents';
import { mdmsCountriesList, nationaliyType } from '../../redux-store/types/mdms';
import { getNationalityList } from '../../redux-store/actions';
import { useSnackbar } from 'notistack';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ConfirmationDialog } from '../commonComponents';
import { SearchableSelect } from '../../lib/formik/searchSelectField';
import { STATE_MASTER } from '../../utils/constant';

type ContactDetailsProps = {
  contactpersons: ContactPerson[];
  address: {
    permanent?: Partial<ApplicantAddressType>;
    correspondence?: Partial<ApplicantAddressType>;
    [key: string]: Partial<ApplicantAddressType> | string | null | undefined | ContactPerson;
  };
};

export type Values = {
  applicants: ContactDetailsProps[];
  saveType: string;
  countryDropdown: string[];
  [key: string]: string | boolean | ContactDetailsProps[] | ContactPerson | string[];
};

export const contactPersonObject: ContactPerson = {
  address1: '',
  address2: '',
  address3: '',
  city: '',
  state: '',
  country: 'INDIA',
  pincode: '',
  name: '',
  landLineNumber: '',
  email: '',
  mobile: '',
  stdCode: '',
  isActive: true,
  countryNameAndCode: 'India: +91',
  countryCode: '+91',
};

const initialValues: Values = {
  applicants: [
    {
      contactpersons: [contactPersonObject],
      address: {
        permanent: getAddressFields('permanent'),
        correspondence: getAddressFields('correspondence'),
      },
    },
  ],
  saveType: 'save and proceed',
  countryDropdown: [],
};

const ApplicantDetails = ({
  values,
  setValues,
  mdmsCountriesList,
  ifXalt,
  setFieldValue,
}: {
  values: Values;
  setValues: FormikHelpers<Values>['setValues'];
  mdmsCountriesList: mdmsCountriesList[];
  ifXalt: boolean;
  setFieldValue: FormikHelpers<Values>['setFieldValue'];
}): JSX.Element => {
  const dispatch = useDispatch();
  const [contactPersonToDelete, setContactPersonToDelete] = useState<{
    contactPerson: ContactPerson;
    ind: number;
    index: number;
  } | null>(null);
  const { application } = useSelector((store: RootStateType) => store.application);
  return (
    <React.Fragment>
      {values.applicants.map((applicant, index) => (
        <React.Fragment key={index}>
          <SubHeading sx={{ mt: '15px' }}>Correspondence Address</SubHeading>
          <Address
            addressType={'correspondence'}
            index={index}
            nationalitiesMdmsResponse={mdmsCountriesList}
            key={values.applicants.length}
            fetchedFromKRACheck={applicant.address.correspondence?.fetchedFromKRA || null}
            applicant={applicant}
            isXalt={ifXalt}
            setFieldValue={setFieldValue}
            values={values.applicants[index].address['correspondence']}
          />
          {!applicant.address.permanent?.fetchedFromKRA &&
            !applicant.address.correspondence?.fetchedFromKRA && (
              <MFCheckbox
                onChange={({ target: { checked } }) =>
                  setValues({
                    ...values,
                    applicants: values.applicants.map((_applicant, _index) => {
                      if (_index === index) {
                        return {
                          ..._applicant,
                          address: {
                            ..._applicant.address,
                            correspondence: {
                              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                              ..._applicant.address.correspondence!,
                              permanentAddressSameAsCorresponding: checked,
                            },
                            ...(!checked && { permanent: getAddressFields('permanent') }),
                          },
                        };
                      }
                      return _applicant;
                    }),
                  })
                }
                name={`applicants.${index}.address.correspondence.permanentAddressSameAsCorresponding`}
                label="Registered Office Address same as Correspondence address."
                sx={{ ml: '40px' }}
              />
            )}
          {!values.applicants[index]?.address?.correspondence
            ?.permanentAddressSameAsCorresponding && (
            <>
              <SubHeading>Registered Office Address</SubHeading>
              <Address
                addressType={'permanent'}
                index={index}
                nationalitiesMdmsResponse={mdmsCountriesList}
                key={values.applicants.length}
                fetchedFromKRACheck={applicant.address.permanent?.fetchedFromKRA || null}
                applicant={applicant}
                isXalt={ifXalt}
                setFieldValue={setFieldValue}
                values={values.applicants[index].address['permanent']}
              />
            </>
          )}
          {values.applicants[index]?.contactpersons?.map((contactPerson, ind) => (
            <React.Fragment key={ind}>
              <SubHeading
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                Details Of {getApplicantName(ind + 1)} Contact Person
                {ind !== 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: 'rgba(196, 42, 51, 0.8)',
                      cursor: 'pointer',
                      pointerEvents: 'all',
                    }}
                    onClick={() =>
                      setContactPersonToDelete({
                        contactPerson,
                        ind,
                        index,
                      })
                    }>
                    <Typography
                      sx={{
                        color: 'rgba(196, 42, 51, 0.8)',
                        fontSize: 13,
                        ml: 'auto',
                        letterSpacing: '-0.2px',
                        cursor: 'pointer',
                      }}>
                      Remove Contact Person{' '}
                    </Typography>
                    <IconButton sx={{ p: '2px' }}>
                      <DeleteOutlineIcon color={'error'} fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </SubHeading>
              <Grid item xs={12} sm={6}>
                <MFTextField
                  name={`applicants.${index}.contactpersons.${ind}.name`}
                  label={`Name of Contact Person *`}
                  placeholder="Enter Name of Contact Person"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MFTextField
                  name={`applicants.${index}.contactpersons.${ind}.mobile`}
                  label={`Mobile Number *`}
                  placeholder="Enter Mobile Number"
                  onKeyDown={(e) => {
                    preventSpecialCharacters(e);
                  }}
                  startAdornment={
                    <CountryCodesDropDown
                      name={`applicants.${index}.contactpersons.${ind}.countryNameAndCode`}
                      value={values.applicants[index].contactpersons[ind].countryNameAndCode || ''}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MFTextField
                  name={`applicants.${index}.contactpersons.${ind}.stdCode`}
                  label={`STD Code`}
                  placeholder="STD Code"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MFTextField
                  name={`applicants.${index}.contactpersons.${ind}.landLineNumber`}
                  placeholder="Enter Landline Number"
                  label={`Landline Number`}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <MFTextField
                  name={`applicants.${index}.contactpersons.${ind}.email`}
                  label={`Email ID *`}
                  placeholder="Enter Email ID"
                />
              </Grid>

              <SubHeading sx={{ backgroundColor: 'white' }}>
                Address Of {getApplicantName(ind + 1)} Contact Person
              </SubHeading>
              <Grid item xs={12} sm={6}>
                <MFTextField
                  name={`applicants.${index}.contactpersons.${ind}.address1`}
                  label={`Address 1 *`}
                  placeholder="Enter Address 1"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MFTextField
                  name={`applicants.${index}.contactpersons.${ind}.address2`}
                  label={`Address 2 *`}
                  placeholder="Enter Address 2"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MFTextField
                  name={`applicants.${index}.contactpersons.${ind}.address3`}
                  label={`Address 3`}
                  placeholder="Enter Address 3"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MFTextField
                  name={`applicants.${index}.contactpersons.${ind}.pincode`}
                  label={`Pincode${
                    ['india', 'indian'].includes(contactPerson.country?.toLowerCase() || '')
                      ? '*'
                      : ''
                  }`}
                  placeholder="Enter Pincode"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MFTextField
                  name={`applicants.${index}.contactpersons.${ind}.city`}
                  label={`City *`}
                  placeholder="Enter City"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {/* <MFTextField
              name={`applicants.${index}.contactpersons.${ind}.country`}
              label={`Country *`}
              placeholder="Enter Country"
            /> */}

                <SearchableSelect
                  name={`applicants.${index}.contactpersons.${ind}.country`}
                  label="Country *"
                  items={mdmsCountriesList.map((nationality) => ({
                    key: nationality.name,
                    value: nationality.name,
                  }))}
                  searchFieldPlaceholder={'Search Country'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {ifXalt && values.applicants[index].contactpersons[index]?.country === 'INDIA' ? (
                  <SearchableSelect
                    name={`applicants.${index}.contactpersons.${ind}.state`}
                    label={`State *`}
                    items={STATE_MASTER.map((state) => ({
                      key: state,
                      value: state,
                    }))}
                    // disabled={isFieldDisabled || fetchedFromKRACheck || false}
                    searchFieldPlaceholder={'Search State'}
                  />
                ) : (
                  <MFTextField
                    name={`applicants.${index}.contactpersons.${ind}.state`}
                    label={`State *`}
                    placeholder="Enter State"
                  />
                )}
              </Grid>
            </React.Fragment>
          ))}
          {(values.applicants[index]?.contactpersons.length as number) < 3 && (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                sx={{
                  color: 'primary.main',
                  fontWeight: 500,
                  fontSize: 14,
                  mt: 2,
                }}
                onClick={() =>
                  setValues({
                    ...values,
                    applicants: values.applicants.map((_applicant, _index) => {
                      return {
                        ..._applicant,
                        contactpersons: [
                          ..._applicant.contactpersons,
                          {
                            ...contactPersonObject,
                            contactperson_type: (_applicant.contactpersons.length + 1).toString(),
                          },
                        ],
                      };
                    }),
                  })
                }>
                Add Contact Person
              </Button>
            </Grid>
          )}
        </React.Fragment>
      ))}
      <ConfirmationDialog
        message={'Are you sure you want to delete Contact Person ?'}
        open={contactPersonToDelete !== null}
        setOpen={() => setContactPersonToDelete(null)}
        onSave={async () => {
          try {
            const { id, applicants } = application || {};
            const updatedApplicants = applicants?.map((applicant, _index: number) => {
              const { contactpersons } = values.applicants[_index] || {};
              if (_index === contactPersonToDelete?.index) {
                return {
                  ...applicant,
                  contactpersons: contactpersons?.map((person, i) => {
                    const noOfcontactpersons = contactpersons?.length;
                    if (i === contactPersonToDelete?.ind) {
                      return {
                        ...person,
                        isActive: false,
                      };
                    }
                    if (noOfcontactpersons === 3 && i === 2) {
                      return {
                        ...person,
                        contactperson_type: '2',
                      };
                    }
                    return person;
                  }),
                };
              }
              return applicant;
            });

            setContactPersonToDelete(null);
            if (id && contactPersonToDelete?.contactPerson.id) {
              await dispatch(
                updateApplication({
                  body: {
                    ...application,
                    applicants: updatedApplicants,
                  },
                  applicationId: id,
                })
              );
              return;
            }
            setValues({
              ...values,
              applicants: values.applicants.map((_applicant, _applicantIndex) => {
                if (_applicantIndex === contactPersonToDelete?.index) {
                  return {
                    ..._applicant,
                    contactpersons: _applicant.contactpersons
                      ?.map((person, i) => {
                        const noOfcontactpersons = _applicant.contactpersons?.length;
                        if (i === contactPersonToDelete?.ind) {
                          return {
                            ...person,
                            isActive: false,
                          };
                        }
                        if (noOfcontactpersons === 3 && i === 2) {
                          return {
                            ...person,
                            contactperson_type: '2',
                          };
                        }
                        return person;
                      })
                      .filter((contactPersons) => contactPersons.isActive),
                  };
                }
                return _applicant;
              }),
            });
          } catch (e) {
            console.error((e as Error).message);
          }
        }}
        onCancel={() => setContactPersonToDelete(null)}
      />
    </React.Fragment>
  );
};

export default function ContactDetails(): JSX.Element {
  const history = useHistory();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [clicked, setClicked] = useState('');
  const { application } = useSelector((store: RootStateType) => store.application);
  const [contactDetails, setContactDetails] = useState(initialValues);
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const [mdmsCountriesList, setMdmsCountriesList] = useState<mdmsCountriesList[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  useEffect(() => {
    const { applicants = [] } = application || {};
    (async function () {
      try {
        const nationalitiesMdmsMasters = (await dispatch(
          getNationalityList()
        )) as unknown as nationaliyType;
        setMdmsCountriesList(nationalitiesMdmsMasters.countries);
        setContactDetails({
          ...contactDetails,
          applicants: applicants.length
            ? applicants.map((applicant) => {
                const correspondence = getAddressData('correspondence', applicant.addresses);
                const permanent = getAddressData('permanent', applicant.addresses);
                const defaultPayload = {
                  contactpersons: applicant.contactpersons
                    ? applicant.contactpersons.map((person) => {
                        return {
                          ...person,
                          countryNameAndCode: person.countryNameAndCode
                            ? person.countryNameAndCode
                            : 'India: +91',
                          country: person.country ? person.country.toUpperCase() : 'INDIA',
                        };
                      })
                    : [{ ...contactPersonObject, contactperson_type: '1' }],
                  address: {
                    correspondence,
                  },
                };
                const permanentAddressPayload = correspondence.permanentAddressSameAsCorresponding
                  ? defaultPayload
                  : {
                      ...defaultPayload,
                      address: {
                        ...defaultPayload.address,
                        permanent: {
                          ...permanent,
                          country: permanent.country ? permanent.country.toUpperCase() : 'INDIA',
                        },
                        correspondence: {
                          ...correspondence,
                          country: correspondence.country
                            ? correspondence.country.toUpperCase()
                            : 'INDIA',
                        },
                      },
                    };
                return permanentAddressPayload;
              })
            : [
                {
                  contactpersons: [{ ...contactPersonObject, contactperson_type: '1' }],
                  address: {
                    permanent: getAddressFields('permanent'),
                    correspondence: getAddressFields('correspondence'),
                  },
                },
              ],
          countryDropdown: nationalitiesMdmsMasters.countries.map((list) => list.name),
        });
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
  }, [application]);

  const handleSubmit = async (values: Values) => {
    try {
      const {
        applicants = [],
        id,
        applicant1ReferenceId = '',
        currentStep,
        applicationNumber,
      } = application || {};
      const { saveType = '' } = values;
      const updatePayload = applicants
        .map((applicant, index: number) => {
          const { address: updatedAddresses = {}, contactpersons } = values.applicants[index] || {};
          return {
            ...applicant,
            contactpersons: contactpersons.map((_person) => {
              return {
                ..._person,
                countryCode: _person.countryNameAndCode?.split(':')[1].trim(),
                address1: removeSingleQuote(_person?.address1),
                address2: removeSingleQuote(_person?.address2),
                address3: removeSingleQuote(_person?.address3),
                city: removeSingleQuote(_person?.city),
                state: ifPushToXalt(application?.pushDataTo || '')
                  ? _person?.state
                  : removeSingleQuote(_person?.state),
                name: removeSingleQuote(_person?.name),
                email: removeSingleQuote(_person?.email),
              };
            }),
            addresses: Object.keys(updatedAddresses)
              .map((addressType) => {
                if (
                  addressType === 'permanent' &&
                  updatedAddresses['correspondence']?.permanentAddressSameAsCorresponding
                ) {
                  // eslint-disable-next-line
                  return permanentAddressSameAsCorresponding(
                    updatedAddresses['correspondence'],
                    addressType,
                    applicant.addresses
                  );
                }
                if (
                  Object.keys(updatedAddresses).length === 1 &&
                  updatedAddresses['correspondence']?.permanentAddressSameAsCorresponding
                ) {
                  return [
                    {
                      ...permanentAddressSameAsCorresponding(
                        updatedAddresses['correspondence'],
                        'permanent',
                        applicant.addresses
                      ),
                    },
                    {
                      ...getAddressData(addressType, applicant.addresses),
                      ...(updatedAddresses[
                        addressType
                      ] as unknown as Partial<ApplicantAddressType>),
                      address_type: addressType,
                    },
                  ];
                }
                return {
                  ...getAddressData(addressType, applicant.addresses),
                  ...(updatedAddresses[addressType] as unknown as Partial<ApplicantAddressType>),
                  address_type: addressType,
                };
              })
              .flat()
              .filter((address) => Object.keys(address).length),
          };
        })
        .map((_applicant) => {
          return {
            ..._applicant,
            addresses: _applicant.addresses.map((address) => {
              return {
                ...address,
                address1: removeSingleQuote(address.address1),
                address2: removeSingleQuote(address.address2),
                address3: removeSingleQuote(address.address3),
                city: removeSingleQuote(address.city),
                state: removeSingleQuote(address.state),
              };
            }),
          };
        });
      const checkApplication = applicationComparison(
        {
          ...application,
          applicants: application?.applicants
            ?.map((applicant) => {
              return {
                ...applicant,
                contactpersons: applicant.contactpersons?.sort(
                  (contactPerson1, contactPerson2) =>
                    Number(contactPerson1.contactperson_type) -
                    Number(contactPerson2.contactperson_type)
                ),
                addresses: applicant.addresses?.sort(
                  (address1, address2) => Number(address1.id) - Number(address2.id)
                ),
              };
            })
            .sort((applicant1, applicant2) => Number(applicant1.id) - Number(applicant2.id)),
        },
        {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          ...application!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          applicants: updatePayload
            .map((applicant) => {
              return {
                ...applicant,
                contactpersons: applicant.contactpersons?.sort(
                  (contactPerson1, contactPerson2) =>
                    Number(contactPerson1.contactperson_type) -
                    Number(contactPerson2.contactperson_type)
                ),
                addresses: applicant?.addresses?.sort(
                  (address1, address2) => Number(address1.id) - Number(address2.id)
                ),
              };
            })
            .sort((applicant1, applicant2) => Number(applicant1.id) - Number(applicant2.id)),
          currentStep: !!currentStep && currentStep > 2 ? currentStep : Number(currentStep) + 1,
        }
      );
      const isSaveLater = saveType !== 'save and proceed';
      if (id && !checkApplication) {
        setLoading(true);
        await dispatch(
          updateApplication({
            body: {
              ...application,
              applicants: updatePayload,
              currentStep: 3,
            },
            applicationId: id,
            ...(isSaveLater && {
              toastMessage: '',
            }),
          })
        );
        !isSaveLater
          ? history.push('investment-payment-details', { id, applicant1ReferenceId })
          : history.push(saveForLater(role, id, applicant1ReferenceId));
      } else if (checkApplication) {
        if (isSaveLater) {
          enqueueSnackbar(`Application ${applicationNumber} - ` + ' Saved successfully', {
            variant: 'success',
            autoHideDuration: 3000,
          });
        }
        !isSaveLater
          ? history.push('investment-payment-details', { id, applicant1ReferenceId })
          : history.push(saveForLater(role, id, applicant1ReferenceId));
      }
    } catch (e) {
      setLoading(false);
      console.error((e as Error).message);
    }
  };

  return (
    <Formik
      initialValues={contactDetails}
      onSubmit={handleSubmit}
      validate={(values: Values) => {
        try {
          validateYupSchema(values, nonIndividualContactDetailsSchema, true, values);
        } catch (e) {
          return yupToFormErrors(e);
        }
      }}
      enableReinitialize={true}>
      {({ handleSubmit, values, setValues, setFieldValue }) => (
        <Grid
          container
          rowSpacing={1}
          // columnSpacing={5}
          sx={{
            width: '100%',
            ml: 0,
            '.MuiGrid-item': { px: { xs: 0, sm: '30px' } },
          }}
          component="form"
          noValidate
          onSubmit={handleSubmit}>
          <ApplicantDetails
            values={values}
            setValues={setValues}
            mdmsCountriesList={mdmsCountriesList}
            ifXalt={ifPushToXalt(application?.pushDataTo || '')}
            setFieldValue={setFieldValue}
          />
          <ProceedSaveLater
            saveLater={
              () => setFieldValue('saveType', 'save for later')
              // setClicked('save for later')
            }
            saveAndProceed={() => setFieldValue('saveType', 'save and proceed')}
            loader={loading}
            clickedButton={values.saveType}
          />
        </Grid>
      )}
    </Formik>
  );
}
